import React, { useState } from 'react';
import { Card, Container, Form, Button, Alert, InputGroup } from 'react-bootstrap';
import { useAuth } from './auth/AuthContext';
import { FaEye, FaEyeSlash } from 'react-icons/fa';

const ChangeCredentials = () => {
  const { role, id } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [cooldown, setCooldown] = useState(0);

  const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';

  const validateForm = () => {
    if (!currentPassword) {
      setError('Current password is required');
      return false;
    }
    
    if (newEmail && !/\S+@\S+\.\S+/.test(newEmail)) {
      setError('Please enter a valid email address');
      return false;
    }
    
    if (newPassword && newPassword.length < 6) {
      setError('New password must be at least 6 characters long');
      return false;
    }
    
    if (newPassword && newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return false;
    }
    
    if (!newEmail && !newPassword) {
      setError('Please provide either a new email or a new password');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    if (!validateForm()) {
      return;
    }
    
    const docId = sessionStorage.getItem("QV_ADMIN_DOC_ID") || localStorage.getItem("QV_ADMIN_DOC_ID") || id;
    
    // Check if we have a valid ID
    if (!docId) {
      setError('Unable to identify admin account. Please log out and log back in.');
      return;
    }
    
    setLoading(true);
    
    try {
      // Determine the correct endpoint based on user role
      let endpoint = '';
      if (role === 'admin') {
        // For main admins, use the admin credentials endpoint
        endpoint = `${API_BASE}/api/admins/admins/${docId}/credentials`;
      } else if (role === 'subadmin') {
        endpoint = `${API_BASE}/api/admins/subadmins/${docId}/credentials`;
      } else if (role === 'subsubadmin') {
        endpoint = `${API_BASE}/api/admins/subsubadmins/${docId}/credentials`;
      } else {
        setError('Invalid user role');
        setLoading(false);
        return;
      }
      
      // Prepare the data to send
      const updateData = {
        currentPassword: currentPassword
      };
      
      // Add new email if provided
      if (newEmail) {
        updateData.email = newEmail;
      }
      
      // Add new password if provided
      if (newPassword) {
        updateData.newPassword = newPassword;
        updateData.confirmPassword = confirmPassword;
      }
      
      // Get admin token for authentication
      const adminToken = localStorage.getItem('adminToken') || sessionStorage.getItem('adminToken');
      
      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify(updateData)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSuccess('Credentials updated successfully!');
        try {
          const { auditLogger } = require('../utils/auditLogger');
          auditLogger.logAction('ADMIN_CHANGE_CREDENTIALS', { role, docId });
        } catch {}

        // Clear form fields
        setCurrentPassword('');
        setNewEmail('');
        setNewPassword('');
        setConfirmPassword('');
        // Reset password visibility
        setShowCurrentPassword(false);
        setShowNewPassword(false);
        setShowConfirmPassword(false);
        
        // 30s submission cooldown
        setCooldown(30);
        const timer = setInterval(() => {
          setCooldown(prev => {
            if (prev <= 1) { clearInterval(timer); return 0; }
            return prev - 1;
          });
        }, 1000);
      } else {
        setError(data.error || 'Failed to update credentials');
      }
    } catch (err) {
      setError('An error occurred while updating credentials. Please try again.');
      console.error('Error updating credentials:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="d-flex align-items-center justify-content-center" style={{ minHeight: '70vh' }}>
      <Card style={{ maxWidth: 500, width: '100%' }}>
        <Card.Body>
          <Card.Title className="mb-3">Change Credentials</Card.Title>
          <Card.Text className="text-muted mb-4">
            Update your email address or password
          </Card.Text>
          
          {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
          {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}
          
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3" controlId="currentPassword">
              <Form.Label>Current Password <span className="text-danger">*</span></Form.Label>
              <InputGroup>
                <Form.Control
                  type={showCurrentPassword ? "text" : "password"}
                  placeholder="Enter current password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                />
                <Button 
                  variant="outline-secondary" 
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                >
                  {showCurrentPassword ? <FaEyeSlash /> : <FaEye />}
                </Button>
              </InputGroup>
            </Form.Group>
            
            <Form.Group className="mb-3" controlId="newEmail">
              <Form.Label>New Email Address</Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter new email address"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
              />
            </Form.Group>
            
            <Form.Group className="mb-3" controlId="newPassword">
              <Form.Label>New Password</Form.Label>
              <InputGroup>
                <Form.Control
                  type={showNewPassword ? "text" : "password"}
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
                <Button 
                  variant="outline-secondary" 
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                </Button>
              </InputGroup>
              <Form.Text className="text-muted">
                Password must be at least 6 characters long
              </Form.Text>
            </Form.Group>
            
            {newPassword && (
              <Form.Group className="mb-3" controlId="confirmPassword">
                <Form.Label>Confirm New Password</Form.Label>
                <InputGroup>
                  <Form.Control
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                  <Button 
                    variant="outline-secondary" 
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                  </Button>
                </InputGroup>
              </Form.Group>
            )}
            
            <div className="d-grid gap-2">
              <Button 
                type="submit" 
                variant="primary" 
                disabled={loading}
              >
                {loading ? 'Updating...' : 'Update Credentials'}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default ChangeCredentials;